import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal, Space, Typography } from 'antd';
import { ButtonProps } from 'antd/lib/button';

import styled, { StyledProp, css } from '@/styled';

import { useNotes } from '../context';

interface NotesPreviewProps extends StyledProp {}

const NotesPreview = ({ className }: NotesPreviewProps) => {
  const navigate = useNavigate();

  const { selectedNote, clearCurrentNote } = useNotes();

  const totalModules = selectedNote?.course.total_modules;
  const totalLessons = selectedNote?.course.total_lessons;

  const handleClickOk = useCallback(() => {
    navigate(`/curso/${selectedNote?.course.hash}/aula/${selectedNote?.lesson.id}`);
  }, [navigate, selectedNote?.course.hash, selectedNote?.lesson.id]);

  const defaultButtonProps: ButtonProps = {
    size: 'large'
  };

  return (
    <Modal
      className={className}
      open={selectedNote !== null}
      onOk={handleClickOk}
      onCancel={clearCurrentNote}
      centered
      closable={false}
      cancelText='Fechar'
      okText='Acessar aula'
      okButtonProps={{ ...defaultButtonProps }}
      cancelButtonProps={{ ...defaultButtonProps, className: 'cancel-button' }}
      maskClosable={false}
      width={600}
      destroyOnClose
    >
      <Space direction='vertical' size={16} className='notes-data'>
        <Space direction='vertical' size={4}>
          <Typography.Title level={3}>{selectedNote?.lesson.title}</Typography.Title>
          <Typography.Text className='notes-preview-description'>{selectedNote?.course.title}</Typography.Text>
          <Space size={12}>
            <Typography.Text type='secondary'>
              {totalModules} módulo{totalModules && totalModules > 1 && 's'}
            </Typography.Text>
            <Typography.Text type='secondary'>
              {totalLessons} aula{totalLessons && totalLessons > 1 && 's'}
            </Typography.Text>
          </Space>
        </Space>
      </Space>
      <div className='notes-preview-content'>
        <Typography.Text>{selectedNote?.notation}</Typography.Text>
      </div>
    </Modal>
  );
};

export default styled(NotesPreview)`
  ${({ theme }) => {
    return css`
      h3.ant-typography {
        margin-bottom: 0;
      }

      .notes-preview-description {
        font-size: ${theme.space.pxToRem(16)};
      }

      .notes-data {
        margin-bottom: ${theme.space.spacing(4)};
      }

      .notes-preview-content {
        overflow: auto;
        max-height: 55vh;
        padding: ${theme.space.spacing(4)};
        border: 1px solid ${theme.variables.notesPreview.border};
        background-color: ${theme.variables.notesPreview.background};
        border-radius: ${theme.borderRadius}px;
        white-space: pre-line;

        .ant-typography {
          font-size: ${theme.space.pxToRem(16)};
        }
      }

      .ant-modal-footer {
        margin-top: ${theme.space.pxToRem(16)};

        .cancel-button {
          background-color: ${theme.palette.alpha(theme.palette.gray[0], 0.08)};
          border-color: ${theme.palette.alpha(theme.palette.gray[0], 0.08)};
        }

        button.ant-btn {
          + button.ant-btn:not(.ant-dropdown-trigger) {
            margin-inline-start: ${theme.space.pxToRem(16)};
          }
        }

        ${theme.breakpoints.down('sm')} {
          display: flex;
          flex-direction: column-reverse;
          gap: ${theme.space.pxToRem(16)};

          button.ant-btn {
            width: 100%;

            + button.ant-btn:not(.ant-dropdown-trigger) {
              margin-inline-start: 0;
            }
          }
        }
      }
    `;
  }}
`;
